import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore, ROL } from './store/auth.store';
import { usePlanFeatures, PlanFeatures } from './hooks/usePlanFeatures';
import { AppLayout } from './components/layout/AppLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { RecepcionPage } from './pages/recepcion/RecepcionPage';
import { AgendaPage } from './pages/agenda/AgendaPage';
import { PacientesPage } from './pages/pacientes/PacientesPage';
import { PacienteFichaPage } from './pages/pacientes/PacienteFichaPage';
import { ExpedientePage } from './pages/expediente/ExpedientePage';
import { ConsultaPage } from './pages/consultas/ConsultaPage';
import { TratamientosPage } from './pages/tratamientos/TratamientosPage';
import { FinanzasPage } from './pages/finanzas/FinanzasPage';
import { ReportesPage } from './pages/reportes/ReportesPage';
import { MarketingPage } from './pages/marketing/MarketingPage';
import { PlantillaEditorPage } from './pages/marketing/PlantillaEditorPage';
import { CampanaFormPage } from './pages/marketing/CampanaFormPage';
import { CampanaDetailPage } from './pages/marketing/CampanaDetailPage';
import { ReferidosPage } from './pages/referidos/ReferidosPage';
import { RedMedicaPage } from './pages/red/RedMedicaPage';
import { PerfilPublicoPage } from './pages/red/PerfilPublicoPage';
import { DrPerfilPage } from './pages/public/DrPerfilPage';
import { PersonalPage } from './pages/personal/PersonalPage';
import { CatalogosPage } from './pages/catalogos/CatalogosPage';
import { ConveniosPage } from './pages/convenios/ConveniosPage';
import { ProductosPage } from './pages/productos/ProductosPage';
import { AjustesPage } from './pages/configuracion/AjustesPage';
import { SuscripcionPage } from './pages/configuracion/SuscripcionPage';
import { PlantillasPage } from './pages/configuracion/PlantillasPage';
import { PersonalizadaEditorPage } from './pages/configuracion/templates/PersonalizadaEditorPage';
import { PlantillasConsentimientoPage } from './pages/configuracion/PlantillasConsentimientoPage';
import { PlantillasExploracionPage } from './pages/configuracion/PlantillasExploracionPage';
import { VademecumGeneralPage } from './pages/configuracion/VademecumGeneralPage';
import { VademecumClinicoPage } from './pages/vademecum/VademecumClinicoPage';
import { TareasPage } from './pages/tareas/TareasPage';
import { SuscripcionGuard } from './components/SuscripcionGuard';
import { SuperAdminGuard } from './components/SuperAdminGuard';
import { SuperAdminLayout } from './components/layout/SuperAdminLayout';
import { SuperAdminDashboardPage } from './pages/super-admin/SuperAdminDashboardPage';
import { SuperAdminClinicasPage } from './pages/super-admin/SuperAdminClinicasPage';
import { SuperAdminMedicosPage } from './pages/super-admin/SuperAdminMedicosPage';
import { AceptarInvitacionPage } from './pages/public/AceptarInvitacionPage';
import { OlvidePasswordPage } from './pages/public/OlvidePasswordPage';
import { RestablecerPasswordPage } from './pages/public/RestablecerPasswordPage';
import { DetallesCitaPage } from './pages/public/DetallesCitaPage';
import { RecetaDigitalPage } from './pages/receta-digital/RecetaDigitalPage';
import { EstudioDigitalPage } from './pages/estudio-digital/EstudioDigitalPage';
import { MiPerfilPage } from './pages/perfil/MiPerfilPage';
import ClinicaArchivadaPage from './pages/ClinicaArchivadaPage';
import { TeleconsultaPage } from './pages/teleconsulta/TeleconsultaPage';
import { TeleconsultaJoinPage } from './pages/teleconsulta/TeleconsultaJoinPage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function ClinicRoute({ children }: { children: React.ReactNode }) {
  const usuario = useAuthStore(s => s.usuario);
  if (!usuario) return <Navigate to="/login" replace />;
  if (usuario.rolId === ROL.SUPER_ADMIN) return <Navigate to="/super-admin" replace />;
  return <>{children}</>;
}

function PlanFeatureRoute({
  feature, children,
}: { feature: keyof PlanFeatures; children: React.ReactNode }) {
  const usuario = useAuthStore(s => s.usuario);
  const { features, isLoading } = usePlanFeatures();

  if (usuario?.rolId === ROL.SUPER_ADMIN) return <>{children}</>;
  if (isLoading) return <>{children}</>;

  const val = features[feature];
  if (val === false || val === 0) {
    return <Navigate to="/configuracion/suscripcion" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/clinica-archivada" element={<ClinicaArchivadaPage />} />
        <Route path="/aceptar-invitacion" element={<AceptarInvitacionPage />} />
        <Route path="/olvide-password" element={<OlvidePasswordPage />} />
        <Route path="/restablecer-password" element={<RestablecerPasswordPage />} />
        <Route path="/receta-digital/:folio" element={<RecetaDigitalPage />} />
        <Route path="/estudio-digital/:folio" element={<EstudioDigitalPage />} />
        <Route path="/dr/:slug" element={<DrPerfilPage />} />
        <Route path="/detalles-cita" element={<DetallesCitaPage />} />
        <Route path="/detalles-cita/:encoded" element={<DetallesCitaPage />} />

        {/* Super Admin — layout y rutas independientes */}
        <Route
          path="/super-admin"
          element={
            <PrivateRoute>
              <SuperAdminGuard>
                <SuperAdminLayout />
              </SuperAdminGuard>
            </PrivateRoute>
          }
        >
          <Route index element={<SuperAdminDashboardPage />} />
          <Route path="clinicas" element={<SuperAdminClinicasPage />} />
          <Route path="medicos"  element={<SuperAdminMedicosPage />} />
        </Route>
        <Route
          path="/"
          element={
            <ClinicRoute>
              <SuscripcionGuard />
              <AppLayout />
            </ClinicRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />

          {/* Módulos principales */}
          <Route path="dashboard"   element={<DashboardPage />} />
          <Route path="recepcion"   element={<RecepcionPage />} />

          {/* Agenda */}
          <Route path="agenda"          element={<AgendaPage />} />
          {/* compatibilidad con rutas antiguas — ahora es la pestaña "Horarios" de Ajustes */}
          <Route path="agenda/horarios" element={<Navigate to="/configuracion?tab=horarios" replace />} />

          {/* Pacientes y expediente */}
          <Route path="pacientes"                   element={<PacientesPage />} />
          <Route path="pacientes/:id"               element={<PacienteFichaPage />} />
          <Route path="pacientes/:id/expediente"    element={<ExpedientePage />} />
          <Route path="consultas/:id"               element={<ConsultaPage />} />

          {/* Tratamientos */}
          <Route path="tratamientos" element={<TratamientosPage />} />

          {/* Vademecum Clínico */}
          <Route path="vademecum" element={<VademecumClinicoPage />} />

          {/* Tareas — parte de todos los planes, sin PlanFeatureRoute */}
          <Route path="tareas" element={<TareasPage />} />

          {/* Finanzas — requiere plan con finanzas=true */}
          <Route path="finanzas" element={<PlanFeatureRoute feature="finanzas"><FinanzasPage /></PlanFeatureRoute>} />
          <Route path="finanzas/corte"       element={<PlanFeatureRoute feature="finanzas"><FinanzasPage /></PlanFeatureRoute>} />
          <Route path="finanzas/cuentas"     element={<PlanFeatureRoute feature="finanzas"><FinanzasPage /></PlanFeatureRoute>} />
          <Route path="finanzas/proveedores"  element={<PlanFeatureRoute feature="finanzas"><FinanzasPage /></PlanFeatureRoute>} />
          <Route path="finanzas/presupuestos" element={<PlanFeatureRoute feature="finanzas"><FinanzasPage /></PlanFeatureRoute>} />
          <Route path="finanzas/fiscal"      element={<PlanFeatureRoute feature="finanzas"><FinanzasPage /></PlanFeatureRoute>} />
          <Route path="finanzas/nuevo"       element={<PlanFeatureRoute feature="finanzas"><FinanzasPage /></PlanFeatureRoute>} />

          {/* Reportes — requiere plan con reportes=true */}
          <Route path="reportes" element={<PlanFeatureRoute feature="reportes"><ReportesPage /></PlanFeatureRoute>} />

          {/* Marketing — requiere plan con marketing=true */}
          <Route path="marketing" element={<PlanFeatureRoute feature="marketing"><MarketingPage /></PlanFeatureRoute>} />
          <Route path="marketing/plantillas" element={<PlanFeatureRoute feature="marketing"><MarketingPage /></PlanFeatureRoute>} />
          <Route path="marketing/plantillas/nueva" element={<PlanFeatureRoute feature="marketing"><PlantillaEditorPage /></PlanFeatureRoute>} />
          <Route path="marketing/plantillas/:id" element={<PlanFeatureRoute feature="marketing"><PlantillaEditorPage /></PlanFeatureRoute>} />
          <Route path="marketing/campanas/nueva" element={<PlanFeatureRoute feature="marketing"><CampanaFormPage /></PlanFeatureRoute>} />
          <Route path="marketing/campanas/:id" element={<PlanFeatureRoute feature="marketing"><CampanaDetailPage /></PlanFeatureRoute>} />

          {/* Red médica */}
          <Route path="red/referidos"   element={<ReferidosPage />} />
          <Route path="red/directorio"  element={<RedMedicaPage />} />
          <Route path="red/mi-perfil"   element={<PerfilPublicoPage />} />
          {/* compatibilidad con rutas antiguas */}
          <Route path="red/contactos"   element={<Navigate to="/red/directorio" replace />} />
          <Route path="referidos"       element={<Navigate to="/red/referidos" replace />} />
          <Route path="red"             element={<Navigate to="/red/referidos" replace />} />

          {/* Mi perfil */}
          <Route path="mi-perfil" element={<MiPerfilPage />} />

          {/* Personal */}
          <Route path="personal" element={<PersonalPage />} />

          {/* Configuración / Ajustes (Clínica + Horarios + Sistema en tabs) */}
          <Route path="configuracion"              element={<AjustesPage />} />
          <Route path="configuracion/suscripcion" element={<SuscripcionPage />} />
          <Route path="configuracion/plantillas"              element={<PlantillasPage />} />
          <Route path="configuracion/plantillas/:tipo/personalizar" element={<PersonalizadaEditorPage />} />
          <Route path="configuracion/consentimientos"        element={<PlantillasConsentimientoPage />} />
          <Route path="configuracion/plantillas-exploracion" element={<PlantillasExploracionPage />} />
          <Route path="configuracion/vademecum"             element={<VademecumGeneralPage />} />
          {/* compatibilidad con rutas antiguas — ahora es la pestaña "Clínica" de Ajustes */}
          <Route path="clinica"                    element={<Navigate to="/configuracion?tab=clinica" replace />} />
          <Route path="clinicas"                   element={<Navigate to="/configuracion?tab=clinica" replace />} />
          <Route path="clinicas/:id"               element={<Navigate to="/configuracion?tab=clinica" replace />} />
          <Route path="configuracion/convenios"    element={<ConveniosPage />} />
          <Route path="configuracion/productos"    element={<ProductosPage />} />
          <Route path="configuracion/catalogo"     element={<CatalogosPage />} />
          {/* compatibilidad con rutas antiguas */}
          <Route path="convenios"         element={<Navigate to="/configuracion/convenios" replace />} />
          <Route path="productos-servicios" element={<Navigate to="/configuracion/productos" replace />} />
          <Route path="catalogos"         element={<Navigate to="/configuracion/catalogo" replace />} />
          <Route path="pagos"             element={<Navigate to="/finanzas" replace />} />
        </Route>
        {/* Teleconsulta - ventana standalone, requiere auth */}
        <Route path="/teleconsulta/:citaId" element={<PrivateRoute><TeleconsultaPage /></PrivateRoute>} />
        <Route path="/teleconsulta/join/:citaId" element={<TeleconsultaJoinPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
